import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, Activity, Clock,
  TrendingUp, ServerCog, PlusCircle, RefreshCw,
  ShieldAlert, Ticket, FileText, Sparkles, X,
} from 'lucide-react';
import {
  AdminApiClient, type DashboardSummary, type AuditLogRow,
} from '../api';
import { useSession } from '../session';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { usePermissions } from '../lib/permissions';
import { humanizeAction } from '../lib/labels';
import { failureFrom, type Failure } from '../lib/api-state';

/**
 * Dashboard — HeroPanel-led layout. Top: 4 primary KPIs (Customers /
 * Active licenses / Heartbeats / Anomalies) inside the hero gradient
 * strip, with 24h sparklines. Below: 3 secondary KPIs in a section
 * card. Then: quick actions tray + recent activity widget side-by-side.
 */
/**
 * V2-009: the band follows the worst unresolved anomaly, not their number. Only `critical` is critical; `warn` is a warning;
 * informational ones are neutral. A server that sends no split (older license server) keeps the old "any = attention" reading.
 */
function anomalyBand(s: DashboardSummary): 'critical' | 'warning' | 'neutral' | 'healthy' {
  if (s.unresolved_anomalies <= 0) return 'healthy';
  const by = s.unresolved_anomalies_by_severity;
  if (!by) return 'critical';
  return by.critical > 0 ? 'critical' : by.warn > 0 ? 'warning' : 'neutral';
}
function anomalyHint(s: DashboardSummary): string {
  if (s.unresolved_anomalies <= 0) return 'all clear';
  const by = s.unresolved_anomalies_by_severity;
  if (!by) return 'attention required';
  if (by.critical > 0) return `${by.critical} critical: attention required`;
  if (by.warn > 0) return `${by.warn} warning${by.warn === 1 ? '' : 's'}`;
  return 'informational only';
}

export function DashboardPage(): JSX.Element {
  const { state } = useSession();
  const nav = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [activity, setActivity] = useState<AuditLogRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [activityErr, setActivityErr] = useState<Failure | null>(null);
  const perms = usePermissions();
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    setRefreshing(true);
    const client = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    Promise.all([
      client.summary().then((s) => { setSummary(s); setErr(null); })
        .catch((e) => { setSummary(null); setErr(failureFrom(e, 'the dashboard summary')); }),
      client.auditLogs({ limit: 10 })
        .then((r) => { setActivity(r.audit_logs); setActivityErr(null); })
        .catch((e) => { setActivity(null); setActivityErr(failureFrom(e, 'recent activity')); }),
    ]).finally(() => setRefreshing(false));
  }, [state.token, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  return (
    <div className="page">
      <SessionGreeting />
      <PageHeader
        title="Dashboard"
        subtitle="Licensing + fleet health at a glance. Click any tile for details."
        icon={Activity}
        accent="#818cf8"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <FetchState
        loading={!summary && !err}
        error={err}
        onRetry={onRefresh}
        skeleton="cards"
      >
        {summary && (
          <>
            <HeroPanel
              icon={Activity}
              accent="#818cf8"
              title="Operations health"
              subtitle="Licensing + fleet activity over the last 24 hours."
              kpis={[
                <HeroKpi key="cust" label="Customers" value={summary.total_customers}
                         hint="total accounts" />,
                <HeroKpi key="lic" label="Active licenses" value={summary.active_licenses}
                         band={summary.active_licenses > 0 ? 'healthy' : 'neutral'} />,
                <HeroKpi key="hb" label="Heartbeats (24h)" value={summary.heartbeats_24h}
                         band={summary.heartbeats_24h > 0 ? 'healthy' : 'warning'}
                         hint={summary.heartbeats_24h > 0 ? 'fleet alive' : 'no signals'} />,
                <HeroKpi key="anom" label="Unresolved anomalies" value={summary.unresolved_anomalies}
                         band={anomalyBand(summary)}
                         hint={anomalyHint(summary)} />,
              ]}
              actions={
                perms.canWrite ? (
                  <>
                    <Button variant="default" size="sm" onClick={() => nav('/customers?new=1')}>
                      <PlusCircle width={14} height={14} />
                      New customer
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => nav('/licenses?issue=1')}>
                      <KeyRound width={14} height={14} />
                      Issue license
                    </Button>
                  </>
                ) : undefined
              }
            />

            <SecondaryMetrics summary={summary} onNav={nav} />

            <div
              style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
                marginTop: 16,
              }}
            >
              <RecentActivity rows={activity} error={activityErr} onRetry={onRefresh} />
              <QuickActions onNav={nav} canWrite={perms.canWrite} />
            </div>
          </>
        )}
      </FetchState>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Secondary metrics — Trials / Conversions / Stale. Only values the
 * server returned are shown: no invented trend lines.
 * ────────────────────────────────────────────────────────────────── */
function SecondaryMetrics({
  summary, onNav,
}: { summary: DashboardSummary; onNav: (path: string) => void }): JSX.Element {
  const tiles: Array<{
    key: string; label: string; value: number; icon: typeof Clock;
    color: string; href: string; band: Band; note: string;
  }> = [
    {
      key: 'trials',
      label: 'Trials expiring (7d)',
      value: summary.trials_expiring_7d,
      icon: Clock,
      color: '#fb923c',
      href: '/trials',
      band: summary.trials_expiring_7d > 0 ? 'warning' : 'neutral',
      note: summary.trials_expiring_7d > 0 ? 'follow up' : 'none expiring',
    },
    {
      key: 'conv',
      label: 'Conversions (30d)',
      value: summary.conversions_30d,
      icon: TrendingUp,
      color: '#2dd4bf',
      href: '/funnel',
      band: summary.conversions_30d > 0 ? 'healthy' : 'neutral',
      note: summary.conversions_30d > 0 ? 'paid conversions' : 'none yet',
    },
    {
      key: 'stale',
      label: 'Stale installations',
      value: summary.stale_installations,
      icon: ServerCog,
      color: summary.stale_installations > 0 ? '#f87171' : '#64748b',
      href: '/servers',
      band: summary.stale_installations > 0 ? 'critical' : 'neutral',
      note: summary.stale_installations > 0 ? 'not reporting' : 'none',
    },
  ];
  return (
    <div
      style={{
        marginTop: 16,
        display: 'grid',
        gap: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      }}
    >
      {tiles.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onNav(t.href)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 16,
            borderRadius: 14,
            border: '1px solid var(--border)',
            background: 'var(--bg-raised)',
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'transform 120ms ease, border-color 120ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              {t.label}
            </div>
            <div
              aria-hidden="true"
              style={{
                width: 28, height: 28, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${t.color}1f`,
              }}
            >
              <t.icon width={14} height={14} color={t.color} />
            </div>
          </div>
          <div
            style={{
              fontSize: '1.75rem',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: t.color,
              lineHeight: 1,
            }}
          >
            {t.value.toLocaleString()}
          </div>
          <StatusChip band={t.band} size="sm" noDot>
            {t.note}
          </StatusChip>
        </button>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Quick actions tray
 * ────────────────────────────────────────────────────────────────── */
function QuickActions({ onNav, canWrite }: { onNav: (path: string) => void; canWrite: boolean }): JSX.Element {
  // Create actions are offered only to roles the server lets create
  // (owner/admin); everyone gets the read-only shortcuts.
  const items: Array<{ icon: typeof PlusCircle; label: string; path: string; tone: string }> = [
    ...(canWrite ? [
      { icon: PlusCircle, label: 'Add customer', path: '/customers?new=1', tone: '#818cf8' },
      { icon: KeyRound, label: 'Issue license', path: '/licenses?issue=1', tone: '#22d3ee' },
    ] : []),
    { icon: ShieldAlert, label: 'Review anomalies', path: '/anomalies', tone: '#f87171' },
    { icon: Ticket, label: 'View tickets', path: '/tickets', tone: '#34d399' },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {items.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => onNav(q.path)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                fontSize: '0.8125rem',
                fontWeight: 500,
                borderRadius: 999,
                border: `1px solid ${q.tone}40`,
                background: `${q.tone}14`,
                color: q.tone,
                cursor: 'pointer',
              }}
            >
              <q.icon width={14} height={14} />
              {q.label}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Recent activity — last 10 audit-log events, severity-tinted by
 * action prefix.
 * ────────────────────────────────────────────────────────────────── */
function RecentActivity({
  rows, error, onRetry,
}: { rows: AuditLogRow[] | null; error: Failure | null; onRetry: () => void }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent activity</CardTitle>
      </CardHeader>
      <CardContent style={{ paddingTop: 0 }}>
        <FetchState
          loading={!rows && !error}
          error={error}
          empty={!!rows && rows.length === 0}
          emptyTitle="No recent activity"
          emptyDescription="Audit log entries will appear here as staff make changes."
          emptyIcon={FileText}
          skeleton="rows"
          onRetry={onRetry}
        >
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' }}>
            {(rows ?? []).map((row) => (
              <ActivityItem key={row.id} row={row} />
            ))}
          </ul>
        </FetchState>
      </CardContent>
    </Card>
  );
}

function ActivityItem({ row }: { row: AuditLogRow }): JSX.Element {
  const band = bandFromAction(row.action);
  const tone =
    band === 'critical' ? '#f87171'
    : band === 'warning' ? '#fbbf24'
    : band === 'degraded' ? '#fb923c'
    : band === 'healthy' ? '#34d399'
    : '#94a3b8';
  return (
    <li
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: 6, height: 6, marginTop: 7,
          borderRadius: 999,
          background: tone,
          boxShadow: `0 0 0 3px ${tone}22`,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <code
            title={row.action}
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {humanizeAction(row.action)}
          </code>
          <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', flex: 'none' }}>
            {fmtRelative(row.created_at)}
          </span>
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {row.actor_type}
          {row.target_type && <> · {row.target_type}{row.target_id ? `:${row.target_id.slice(0, 8)}…` : ''}</>}
          {row.reason && <> · {row.reason}</>}
        </div>
      </div>
    </li>
  );
}

function bandFromAction(action: string): Band {
  if (/^(delete_|revoke_|suspend_|yank_)/.test(action)) return 'critical';
  if (/^(disable_|deactivate_)/.test(action)) return 'warning';
  if (/^(update_|change_|patch_|edit_)/.test(action)) return 'degraded';
  if (/^(create_|issue_|resume_|enable_|approve_)/.test(action)) return 'healthy';
  return 'neutral';
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ──────────────────────────────────────────────────────────────────
 * Session greeting banner — shown once per sign-in.
 *
 * SignIn.tsx writes the email/role/timestamp to sessionStorage on
 * successful auth; we read once on mount, render a transient banner,
 * and clear the key so a refresh doesn't re-flash.
 *
 * Backend does not yet expose previous-login IP — when it lands the
 * banner copy can include "from <ip> · <when>". For now it confirms
 * the current session and time.
 * ────────────────────────────────────────────────────────────────── */

interface GreetingPayload {
  email: string;
  role: string;
  at: number;
}

function SessionGreeting(): JSX.Element | null {
  const [greeting, setGreeting] = useState<GreetingPayload | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      const raw = sessionStorage.getItem('vloud:session_greeting');
      if (!raw) return undefined;
      sessionStorage.removeItem('vloud:session_greeting');
      const parsed = JSON.parse(raw) as GreetingPayload;
      // Only show if the stamp is fresh (≤60s) — protects against
      // stale sessionStorage from a previous tab.
      if (Date.now() - parsed.at > 60_000) return undefined;
      setGreeting(parsed);
      const id = setTimeout(() => setGreeting(null), 12_000);
      return () => clearTimeout(id);
    } catch {
      return undefined;
    }
  }, []);

  if (!greeting) return null;

  const firstName = greeting.email.split(/[@.+_-]/)[0]
    ?.replace(/^./, (c) => c.toUpperCase()) ?? 'there';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginBottom: 12,
        padding: '10px 14px',
        borderRadius: 12,
        border: '1px solid rgba(99, 102, 241, 0.35)',
        background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.10), rgba(167, 139, 250, 0.05))',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        aria-hidden
        style={{
          flex: 'none',
          width: 28, height: 28, borderRadius: 8,
          background: 'rgba(99, 102, 241, 0.18)',
          color: 'var(--accent-text)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Sparkles width={14} height={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Welcome back, {firstName}.
        </div>
        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          Signed in as {greeting.email} · {greeting.role} role · {fmtClock(greeting.at)}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setGreeting(null)}
        aria-label="Dismiss"
        style={{
          flex: 'none',
          background: 'transparent', border: 'none',
          color: 'var(--text-muted)', cursor: 'pointer',
          padding: 4, borderRadius: 6,
        }}
      >
        <X width={14} height={14} />
      </button>
    </div>
  );
}

function fmtClock(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

